import React from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {Box, BoxProps, Skeleton, SkeletonProps, Stack, StackProps, TabsProps, Text, TextProps, useMultiStyleConfig} from '@chakra-ui/react'
import {Tabs, TabsSkeleton} from '_uiComponents'
import {ProductListingVisualization} from '_types'

interface SearchResultsHeaderProps {
    searchQuery: string | null
    productsCount: number
    storiesCount?: number
    tabIndex: ProductListingVisualization
    isLoading?: boolean
    onTabChange: (index: ProductListingVisualization) => void
}

/**
 * @name SearchResultsHeader
 *
 * @component
 *
 * @description
 * Displays the search results header, including the search query, number of products found,
 * and tabs for navigating between products and stories.
 *
 * @param {SearchResultsHeaderProps} props - The props for the component.
 * @param {string} props.searchQuery - The search query entered by the user.
 * @param {number} props.productsCount - The number of products found for the search query.
 * @param {number} [props.storiesCount] - The number of stories found for the search query.
 * @param {ProductListingVisualization} props.tabIndex - The index of the currently active tab.
 * @param {boolean} [props.isLoading=false] - Indicates if to render the search results header skeleton.
 * @param {function} props.onTabChange - Callback function to handle tab changes.
 *
 * @returns {JSX.Element} - The rendered SearchResultsHeader component.
 */
const SearchResultsHeader: React.FC<SearchResultsHeaderProps> = ({searchQuery, productsCount, storiesCount = 0, tabIndex, isLoading = false, onTabChange}) => {
    const styles = useMultiStyleConfig('SearchResultsHeader') as Record<string, BoxProps & StackProps & TextProps & TabsProps & SkeletonProps>
    const {formatMessage} = useIntl()

    const productsLabel = formatMessage(
        {id: 'tab.nav.search.productsresults', defaultMessage: 'Products ( {0} )'},
        {
            0: productsCount,
        },
    )

    const storiesLabel = formatMessage(
        {id: 'tab.nav.search.artclesresults', defaultMessage: 'Stories ( {0} )'},
        {
            0: storiesCount,
        },
    )

    const tabsMock = [{label: productsLabel}, {label: storiesLabel, isDisabled: storiesCount === 0}]

    return (
        <Box data-testid="search-results-header" {...styles.resultsHeader}>
            <Stack {...styles.resultsHeaderContent}>
                {isLoading ? (
                    <>
                        <Skeleton data-testid="title-skeleton" {...styles.resultsTitleSkeleton} />
                        <TabsSkeleton tabsCount={2} />
                    </>
                ) : (
                    <>
                        <Text as="h1" {...styles.resultsTitle}>
                            <FormattedMessage
                                id={'tab.nav.search.totalresults'}
                                defaultMessage={'{0} results for "{1}"'}
                                values={{0: productsCount + storiesCount, 1: searchQuery}}
                            />
                        </Text>
                        <Tabs size="sm" align="start" tabs={tabsMock} activeIndex={tabIndex} onTabChange={onTabChange} />
                    </>
                )}
            </Stack>
        </Box>
    )
}

export default SearchResultsHeader
